import React from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, Progress, List, Avatar, Tag, Space } from 'antd';
import {
  ApiOutlined,
  AppstoreOutlined,
  UserOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  CustomerServiceOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { db, supabase, functions } from '../../../shared/lib/supabase';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export const AdminDashboardPage: React.FC = () => {
  // Fetch N8N instances count
  const { data: instancesCount = 0, isLoading: loadingInstances } = useQuery({
    queryKey: ['dashboard-instances-count'],
    queryFn: async () => {
      const { data, error } = await db.n8nInstances.list();
      if (error) throw error;
      return data?.length || 0;
    },
  });

  // Fetch active products count
  const { data: productsData, isLoading: loadingProducts } = useQuery({
    queryKey: ['dashboard-products-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('is_enabled, marketplace_status');
      if (error) throw error;
      
      const active = data?.filter(p => p.is_enabled).length || 0;
      const published = data?.filter(p => p.marketplace_status === 'published').length || 0;
      const total = data?.length || 0;
      
      return { active, published, total };
    },
  });

  // Fetch clients stats
  const { data: clientsData, isLoading: loadingClients } = useQuery({
    queryKey: ['dashboard-clients-stats'],
    queryFn: async () => {
      try {
        const response = await functions.listClients();
        if (response.success) {
          const clients = response.clients || [];
          const thisMonth = clients.filter((c: any) => 
            dayjs(c.created_at).isAfter(dayjs().startOf('month'))
          ).length;
          return { total: clients.length, thisMonth };
        }
        return { total: 0, thisMonth: 0 };
      } catch (error) {
        console.error('Error in clients stats:', error);
        return { total: 0, thisMonth: 0 };
      }
    },
  });

  // Fetch contracted products stats
  const { data: contractedData, isLoading: loadingContracted } = useQuery({
    queryKey: ['dashboard-contracted-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_products')
        .select('is_active, created_at');
      if (error) throw error;
      
      const active = data?.filter(p => p.is_active).length || 0;
      const total = data?.length || 0;
      const thisMonth = data?.filter(p => 
        dayjs(p.created_at).isAfter(dayjs().startOf('month'))
      ).length || 0;
      
      return { active, total, thisMonth };
    },
  });

  // Fetch support tickets stats
  const { data: supportData, isLoading: loadingSupport } = useQuery({
    queryKey: ['dashboard-support-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('status, priority');
      if (error) throw error;
      
      const open = data?.filter(t => t.status === 'open').length || 0;
      const inProgress = data?.filter(t => t.status === 'in_progress').length || 0;
      const high = data?.filter(t => t.priority === 'high' && t.status !== 'closed').length || 0;
      const total = data?.length || 0;
      
      return { open, inProgress, high, total };
    },
  });

  // Fetch payments stats
  const { data: paymentsData, isLoading: loadingPayments } = useQuery({
    queryKey: ['dashboard-payments-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_history')
        .select('status, amount, created_at');
      if (error) throw error;
      
      const thisMonth = data?.filter(p => 
        dayjs(p.created_at).isAfter(dayjs().startOf('month'))
      ) || [];
      
      const paid = thisMonth.filter(p => p.status === 'paid');
      const pending = thisMonth.filter(p => p.status === 'pending');
      
      const revenue = paid.reduce((sum, p) => sum + (p.amount || 0), 0);
      const pendingAmount = pending.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      return { revenue, pendingAmount, paidCount: paid.length, pendingCount: pending.length };
    },
  });

  // Recent activities: Use real activity_log schema with actual field names
  const { data: recentActivities, isLoading: loadingActivities } = useQuery({
    queryKey: ['dashboard-recent-activities'],
    queryFn: async () => {
      // Try reading from activity_log first (real schema: user_id, client_product_id, action_type, description, metadata)
      try {
        console.log('[Dashboard] Attempting to fetch from activity_log...');
        const { data: activities, error: activitiesErr } = await supabase
          .from('activity_log')
          .select('id, user_id, client_product_id, action_type, description, metadata, created_at')
          .order('created_at', { ascending: false })
          .limit(10);

        console.log('[Dashboard] activity_log query result:', { 
          activitiesCount: activities?.length, 
          activities, 
          activitiesErr,
          errorCode: (activitiesErr as any)?.code,
          errorMessage: (activitiesErr as any)?.message
        });

        if (!activitiesErr && activities && activities.length > 0) {
          console.log('[Dashboard] Using activity_log data, found', activities.length, 'activities');
          const userIds = Array.from(new Set((activities || []).map((a: any) => a.user_id).filter(Boolean)));
          const clientProductIds = Array.from(new Set((activities || []).map((a: any) => a.client_product_id).filter(Boolean)));

          console.log('[Dashboard] Extracted IDs:', { userIds, clientProductIds });

          const profilesMap: Record<string, any> = {};
          const clientProductMap: Record<string, any> = {};

          // Get user profiles (who did the action)
          if (userIds.length > 0) {
            const { data: profilesData, error: profilesErr } = await supabase
              .from('user_profiles')
              .select('id, full_name')
              .in('id', userIds);
            console.log('[Dashboard] User profiles query:', { profilesData, profilesErr });
            if (!profilesErr && profilesData) profilesData.forEach((p: any) => { profilesMap[String(p.id)] = p; });
          }

          // Get client_products with related product and client info
          if (clientProductIds.length > 0) {
            const { data: cpRaw, error: cpRawErr } = await supabase
              .from('client_products')
              .select('id, client_id, product_id, products(id, name)')
              .in('id', clientProductIds);
            console.log('[Dashboard] Client products query:', { cpRaw, cpRawErr });
            if (!cpRawErr && cpRaw) {
              // Collect client IDs from cpRaw to fetch user_profiles
              const cpClientIds = Array.from(new Set(cpRaw.map((cp: any) => cp.client_id).filter(Boolean)));
              console.log('[Dashboard] Extracted client IDs from client_products:', cpClientIds);
              if (cpClientIds.length > 0) {
                const { data: clientProfiles, error: clientProfilesErr } = await supabase
                  .from('user_profiles')
                  .select('id, full_name')
                  .in('id', cpClientIds);
                console.log('[Dashboard] Client profiles query:', { clientProfiles, clientProfilesErr });
                if (!clientProfilesErr && clientProfiles) {
                  clientProfiles.forEach((p: any) => { profilesMap[String(p.id)] = p; });
                }
              }
              cpRaw.forEach((cp: any) => {
                clientProductMap[String(cp.id)] = {
                  clientName: profilesMap[String(cp.client_id)]?.full_name || 'Cliente desconhecido',
                  productName: cp.products?.name || 'Produto desconhecido',
                };
              });
            }
          }

          console.log('[Dashboard] Maps after enrichment:', { profilesMap, clientProductMap });

          // If there are UUIDs embedded in descriptions not covered by user_id or client_product_id,
          // collect them and try to resolve to user profiles.
          const uuidRegex = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
          const extraIds = new Set<string>();
          (activities || []).forEach((a: any) => {
            const text = a.description || '';
            const matches = text.match(uuidRegex) || [];
            matches.forEach((m: string) => {
              if (!profilesMap[String(m)] && !clientProductMap[String(m)]) extraIds.add(m);
            });
          });

          if (extraIds.size > 0) {
            const idsToQuery = Array.from(extraIds);
            console.log('[Dashboard] Extra IDs to resolve:', idsToQuery);
            const { data: extraProfiles, error: extraProfilesErr } = await supabase
              .from('user_profiles')
              .select('id, full_name')
              .in('id', idsToQuery);
            console.log('[Dashboard] Extra profiles query:', { extraProfiles, extraProfilesErr });
            if (!extraProfilesErr && extraProfiles) {
              extraProfiles.forEach((p: any) => { profilesMap[String(p.id)] = p; });
            }
          }

          const actionText = (action: string) => {
            const actionLower = action?.toLowerCase() || '';
            switch (actionLower) {
              case 'contract_created': return 'contratou';
              case 'contract_cancelled': return 'cancelou contrato';
              case 'payment_made': return 'realizou pagamento';
              case 'form_submission': return 'enviou um formulário';
              case 'form_updated': return 'atualizou formulário';
              case 'form_approved': return 'aprovou formulário';
              case 'form_rejected': return 'rejeitou formulário';
              case 'workflow_activated': return 'ativou workflow';
              case 'workflow_deactivated': return 'desativou workflow';
              case 'login': return 'fez login';
              case 'logout': return 'fez logout';
              default: return action?.replace(/_/g, ' ') || 'executou ação';
            }
          };

          const replaceIdsInText = (text: string) => {
            if (!text) return '';
            return text.replace(uuidRegex, (match) => {
              // Replace user IDs with profile names
              if (profilesMap[String(match)]) return profilesMap[String(match)].full_name || match;
              // Replace client_product IDs with product name or client name
              if (clientProductMap[String(match)]) return clientProductMap[String(match)].productName || clientProductMap[String(match)].clientName || match;
              return match;
            });
          };

          const result = (activities || []).map((a: any) => {
            const firstUuidInDesc = (a.description || '').match(uuidRegex)?.[0] || null;
            return {
              ...a,
              actor: profilesMap[String(a.user_id)] || (firstUuidInDesc ? profilesMap[String(firstUuidInDesc)] : null) || null,
              clientProduct: clientProductMap[String(a.client_product_id)] || null,
              actionText: actionText(a.action_type),
              displayDescription: replaceIdsInText(a.description || ''),
            };
          });

          console.log('[Dashboard] Final activities result:', result);
          return result;
        }
      } catch (e) {
        // If activity_log is empty or error, fallback to client_products
        console.warn('activity_log query returned no results, using fallback:', (e as any)?.message || e);
      }

      // Fallback: show recent client_products contracts
      const { data, error } = await supabase
        .from('client_products')
        .select('id, client_id, product_id, is_active, created_at, products(id, name)')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      const clientIds = Array.from(new Set((data || []).map((r: any) => r.client_id).filter(Boolean)));
      const profilesMap: Record<string, any> = {};
      if (clientIds.length > 0) {
        const { data: profilesData, error: profilesErr } = await supabase
          .from('user_profiles')
          .select('id, full_name')
          .in('id', clientIds);
        if (!profilesErr && profilesData) {
          profilesData.forEach((p: any) => { profilesMap[String(p.id)] = p; });
        }
      }

      const enriched = (data || []).map((r: any) => {
        const clientId = r.client_id;
        return {
          ...r,
          actor: profilesMap[String(clientId)] || null,
          clientProduct: {
            clientName: profilesMap[String(clientId)]?.full_name || 'Cliente desconhecido',
            productName: r.products?.name || 'Produto desconhecido',
          },
          actionText: r.is_active ? 'contratou' : 'removeu/alterou contrato',
          action_type: r.is_active ? 'contract_created' : 'contract_modified',
        };
      });

      return enriched || [];
    },
  });

  const isLoading = loadingInstances || loadingProducts || loadingClients || loadingContracted || loadingSupport || loadingPayments;

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 8 }}>Dashboard</Title>
        <Text type="secondary">Visão geral do sistema e métricas principais</Text>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Métricas Principais */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Instâncias N8N"
                  value={instancesCount}
                  prefix={<ApiOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Servidores ativos
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Produtos Ativos"
                  value={productsData?.active || 0}
                  suffix={`/ ${productsData?.total || 0}`}
                  prefix={<AppstoreOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
                <div style={{ marginTop: 12 }}>
                  <Progress 
                    percent={Math.round(((productsData?.active || 0) / (productsData?.total || 1)) * 100)} 
                    size="small" 
                    showInfo={false}
                  />
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total de Clientes"
                  value={clientsData?.total || 0}
                  prefix={<UserOutlined style={{ color: '#722ed1' }} />}
                  valueStyle={{ color: '#722ed1' }}
                />
                <div style={{ marginTop: 12 }}>
                  <Space>
                    <Tag icon={<RiseOutlined />} color="success">
                      +{clientsData?.thisMonth || 0} este mês
                    </Tag>
                  </Space>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Contratos Ativos"
                  value={contractedData?.active || 0}
                  suffix={`/ ${contractedData?.total || 0}`}
                  prefix={<CheckCircleOutlined style={{ color: '#13c2c2' }} />}
                  valueStyle={{ color: '#13c2c2' }}
                />
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {contractedData?.thisMonth || 0} novos este mês
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Financeiro e Suporte */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <Card 
                title={
                  <Space>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    <span>Financeiro (Mês Atual)</span>
                  </Space>
                }
                bordered={false}
              >
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Receita Confirmada</Text>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}>
                    {formatCurrency(paymentsData?.revenue || 0)}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Aguardando Pagamento</Text>
                  <div style={{ fontSize: 18, fontWeight: 500, color: '#faad14' }}>
                    {formatCurrency(paymentsData?.pendingAmount || 0)}
                  </div>
                </div>
                <Space split={<span>•</span>}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {paymentsData?.paidCount || 0} pagos
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {paymentsData?.pendingCount || 0} pendentes
                  </Text>
                </Space>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card 
                title={
                  <Space>
                    <CustomerServiceOutlined style={{ color: '#1890ff' }} />
                    <span>Chamados de Suporte</span>
                  </Space>
                }
                bordered={false}
              >
                <Row gutter={[8, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Abertos"
                      value={supportData?.open || 0}
                      valueStyle={{ fontSize: 20, color: '#fa8c16' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Em Progresso"
                      value={supportData?.inProgress || 0}
                      valueStyle={{ fontSize: 20, color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Urgentes"
                      value={supportData?.high || 0}
                      valueStyle={{ fontSize: 20, color: '#f5222d' }}
                    />
                  </Col>
                </Row>
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Total: {supportData?.total || 0} tickets
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card 
                title={
                  <Space>
                    <ThunderboltOutlined style={{ color: '#722ed1' }} />
                    <span>Marketplace</span>
                  </Space>
                }
                bordered={false}
              >
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary">Produtos Publicados</Text>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#722ed1' }}>
                    {productsData?.published || 0}
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <Progress 
                    percent={Math.round(((productsData?.published || 0) / (productsData?.total || 1)) * 100)}
                    strokeColor="#722ed1"
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {productsData?.total || 0} produtos totais
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Atividades Recentes */}
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <Card 
                title={
                  <Space>
                    <ClockCircleOutlined style={{ color: '#1890ff' }} />
                    <span>Atividades Recentes</span>
                  </Space>
                }
                bordered={false}
              >
                {loadingActivities ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin />
                  </div>
                ) : (
                  <List
                    dataSource={recentActivities || []}
                    locale={{ emptyText: 'Nenhuma atividade recente' }}
                    renderItem={(item: any) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={<Avatar icon={<AppstoreOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                          title={
                            <Space>
                              <Text strong style={{ color: '#1890ff' }}>
                                {item.actor?.full_name || 'Admin'}
                              </Text>
                              <Text type="secondary">{item.actionText || 'executou'}</Text>
                              {item.clientProduct?.productName && (
                                <Text strong style={{ color: '#52c41a' }}>
                                  {item.clientProduct.productName}
                                </Text>
                              )}
                              {item.clientProduct?.clientName && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  para {item.clientProduct.clientName}
                                </Text>
                              )}
                            </Space>
                          }
                          description={
                            <Space split={<span>•</span>}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(item.created_at).format('DD/MM/YYYY HH:mm')}
                              </Text>
                              {item.displayDescription ? (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {item.displayDescription}
                                </Text>
                              ) : item.description ? (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {item.description}
                                </Text>
                              ) : null}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </Space>
      )}
    </div>
  );
};
